/**
 * Pure helpers for lifecycle stage/transition resolution and the side effects
 * every ticket write should apply (FIRST_INTERACTION transition +
 * contributors update). This module is the ONE home for the
 * "active stage from ticketStatus" idiom — `TicketAccessContext`,
 * `TicketLifecycleContext`, and `TicketWriterContext` all resolve through
 * here rather than re-deriving it.
 */
import type { LifecycleConfig, LifecycleTransition } from './lifecycleTypes'
import {
  findMatchingTransition,
  getDefaultStage,
  LifecycleTriggerType,
} from './lifecycleTypes'

/**
 * The stage a ticket is actually in: its `ticketStatus`, or — for
 * hand-crafted/imported tickets that never had one written — the config's
 * default stage. Empty string when the config has no default either.
 */
export function resolveActiveStageId(
  lifecycleConfig: LifecycleConfig,
  ticketStatus: string | undefined
): string {
  return ticketStatus || getDefaultStage(lifecycleConfig)?.id || ''
}

type TransitionTrigger = Parameters<typeof findMatchingTransition>[2]

/**
 * `findMatchingTransition` keyed off the ACTIVE stage (see
 * `resolveActiveStageId`) instead of the raw `ticketStatus`. Callers inside
 * writer enqueue closures should pass the writer's `latest.ticketStatus` —
 * not render-time state — so queued writes that already advanced the stage
 * are reflected.
 */
export function findTransitionFromStatus(
  lifecycleConfig: LifecycleConfig,
  ticketStatus: string | undefined,
  trigger: TransitionTrigger
): LifecycleTransition | null {
  return findMatchingTransition(
    lifecycleConfig,
    resolveActiveStageId(lifecycleConfig, ticketStatus),
    trigger
  )
}

export interface ComputeInteractionSideEffectsArgs {
  lifecycleConfig: LifecycleConfig | undefined
  ticketStatus: string
  contributors: ReadonlyArray<string>
  userId: string | undefined
}

export interface InteractionSideEffects {
  /** Set when the FIRST_INTERACTION transition fires; undefined otherwise. */
  ticketStatus?: string
  /** Set when the current user isn't already present in `contributors`. */
  contributors?: string[]
}

/**
 * Compute the contributor + lifecycle-transition deltas that should land on a
 * ticket write that counts as "the user interacted with this ticket".
 * Idempotent: calling it when the user is already a contributor / the
 * transition has already fired returns the corresponding field undefined so the
 * caller can skip emitting a no-op patch.
 */
export const computeInteractionSideEffects = ({
  lifecycleConfig,
  ticketStatus,
  contributors,
  userId,
}: ComputeInteractionSideEffectsArgs): InteractionSideEffects => {
  const out: InteractionSideEffects = {}

  if (userId && !contributors.includes(userId)) {
    out.contributors = [...contributors, userId]
  }

  if (lifecycleConfig) {
    const transition = findTransitionFromStatus(lifecycleConfig, ticketStatus, {
      type: LifecycleTriggerType.FIRST_INTERACTION,
    })
    if (transition) {
      out.ticketStatus = transition.to
    }
  }

  return out
}
